import React from 'react';
import { Typography, Grid, TextField, Box, MenuItem } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import SetPageTitle from 'components/Portals/SetPageTitle';
import SetPageNavAction from 'components/Portals/SetPageNavAction';
import Container from 'components/Container';
import ContainerBox from 'components/ContainerBox';
import { getLanguageNativeName } from 'common/locales/utils';
import { UserApi } from 'common/types';
import { useForm, Controller } from 'react-hook-form';
import { Prompt } from 'react-router';

import { useSelector } from 'react-redux';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as LanguageSelectors from 'selectors/LanguageSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import { api } from 'common/frontend/api';

import SaveButton from 'common/components/SaveButton';

import { Routes } from 'routing';
import { useTranslation } from 'services/localization';

type FormValues = Partial<UserApi>;

const Profile = () => {
	const classes = useStyles();
	const { t } = useTranslation();

	const user = useSelector(UserSelectors.userData)!;
	const activeLanguage = useSelector(LanguageSelectors.language);
	const languagesAvailable = useSelector(ShopSelectors.activeShopLanguagesAvailable);

	const userForm = useForm<FormValues>({
		defaultValues: user ?? {},
	});

	const { isDirty, isSubmitting } = userForm.formState;

	const onSubmit = async (data: FormValues) => {
		await api().users.doc(user.id).update(data);
		userForm.reset(data);
	};

	return (
		<>
			<SetPageTitle
				priority={2}
				title={t('account.profile.pageTitle', 'Profile')}
				rightContent={
					<SaveButton
						disabled={!isDirty}
						loading={isSubmitting}
						onClick={userForm.handleSubmit(onSubmit)}
						t={t}
					/>
				}
			/>
			<SetPageNavAction
				priority={2}
				backAction={{ to: Routes.ACCOUNT, title: t('account.pageTitle', 'Account') }}
				quickLinks={[]}
			/>
			<Prompt when={isDirty} message={() => t('common:prompts.unsavedChanges')} />
			<Container>
				<ContainerBox>
					<Grid container spacing={2}>
						<Grid item xs={12} md={6}>
							<Typography variant="h6">{t('account.profile.details', 'Details')}</Typography>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField label={t('common:form.email')} value={user?.email} fullWidth disabled />
							<Box mt={4} />
							<Grid container spacing={2}>
								<Grid item xs={12} sm={6}>
									<Controller
										control={userForm.control}
										name="firstName"
										defaultValue=""
										render={(renderProps) => (
											<TextField
												variant="outlined"
												label={t('common:form.firstName')}
												onChange={(e) => renderProps.field.onChange(e.target.value)}
												value={renderProps.field.value}
												fullWidth
											/>
										)}
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<Controller
										control={userForm.control}
										name="lastName"
										defaultValue=""
										render={(renderProps) => (
											<TextField
												variant="outlined"
												label={t('common:form.lastName')}
												value={renderProps.field.value}
												onChange={(e) => renderProps.field.onChange(e.target.value)}
												fullWidth
											/>
										)}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</ContainerBox>
				<ContainerBox>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6}>
							<Typography variant="h6">
								{t('account.profile.preferences', 'Preferences')}
							</Typography>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Controller
								control={userForm.control}
								name="language"
								defaultValue={activeLanguage}
								render={(renderProps) => (
									<TextField
										label={t('account.profile.systemLanguage', 'System language')}
										fullWidth
										helperText={t(
											'account.profile.systemLanguageHelper',
											'The language used in Rentle admin (for you)',
										)}
										value={renderProps.field.value}
										onChange={(e) => renderProps.field.onChange(e.target.value)}
										select
										SelectProps={{
											classes: {
												select: classes.languageItem,
											},
										}}
									>
										{languagesAvailable.map((lang) => (
											<MenuItem key={lang} value={lang} className={classes.languageItem}>
												{getLanguageNativeName(lang)}
											</MenuItem>
										))}
									</TextField>
								)}
							/>
						</Grid>
					</Grid>
				</ContainerBox>
			</Container>
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		languageItem: {
			textTransform: 'capitalize',
		},
	}),
);

export default Profile;
